import { useEffect, useState } from "react";
import { http, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Sparkles, Copy, Check, Lock, Zap } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function PromptGenerator() {
  const { user } = useAuth();
  const [topic, setTopic] = useState("");
  const [goal, setGoal] = useState("Short-form video script");
  const [tone, setTone] = useState("punchy, cinematic");
  const [output, setOutput] = useState("");
  const [busy, setBusy] = useState(false);
  const [typed, setTyped] = useState("");
  const [copied, setCopied] = useState(false);
  const [quota, setQuota] = useState(null);
  const [paywall, setPaywall] = useState(false);

  const refreshQuota = async () => {
    try {
      const { data } = await http.get("/ai/quota");
      setQuota(data);
    } catch {}
  };

  useEffect(() => { refreshQuota(); }, [user]);

  useEffect(() => {
    if (!output) return;
    let i = 0;
    setTyped("");
    const id = setInterval(() => {
      i += 6;
      setTyped(output.slice(0, i));
      if (i >= output.length) clearInterval(id);
    }, 18);
    return () => clearInterval(id);
  }, [output]);

  const run = async () => {
    if (!topic.trim()) { toast.error("Give me a topic first."); return; }
    setBusy(true); setOutput(""); setTyped("");
    try {
      const { data } = await http.post("/ai/prompt-generator", { topic, goal, tone });
      setOutput(data.prompt);
      setQuota(data.quota);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 402 || detail?.code === "quota_exceeded") {
        setPaywall(true);
        setQuota({ used: detail.used, limit: detail.limit, remaining: 0, unlimited: false });
      } else {
        toast.error(formatApiError(detail) || "Failed to generate");
      }
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(output);
    setCopied(true);
    toast.success("Copied.");
    setTimeout(() => setCopied(false), 1500);
  };

  const unlimited = quota?.unlimited;
  const remaining = quota?.remaining ?? null;

  return (
    <div className="glass rounded-2xl p-6 lg:p-8 relative" data-testid="prompt-generator">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Sparkles className="text-[#00FFC2]" size={18} />
          <span className="label-eyebrow">Prompt Lab · Live</span>
        </div>
        {quota && (
          <div className="text-xs tracking-wide" data-testid="prompt-gen-quota">
            {unlimited ? (
              <span className="inline-flex items-center gap-1.5 text-[#00FFC2]"><Zap size={12} /> Unlimited</span>
            ) : (
              <span className="text-gray-400">
                <span className={remaining === 0 ? "text-[#A855F7]" : "text-white"}>{remaining}</span>
                <span className="text-gray-500"> / {quota.limit} today</span>
              </span>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="text-xs text-gray-400 mb-1.5 block">Topic</label>
          <input
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="e.g. building a faceless YouTube channel about ancient philosophy"
            data-testid="prompt-gen-topic"
            className="w-full bg-black/40 border hairline rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#00FFC2]/60"
          />
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1.5 block">Goal</label>
          <input value={goal} onChange={(e) => setGoal(e.target.value)} data-testid="prompt-gen-goal"
            className="w-full bg-black/40 border hairline rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#00FFC2]/60" />
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1.5 block">Tone</label>
          <input value={tone} onChange={(e) => setTone(e.target.value)} data-testid="prompt-gen-tone"
            className="w-full bg-black/40 border hairline rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#00FFC2]/60" />
        </div>
      </div>

      <button
        onClick={run}
        disabled={busy}
        data-testid="ai-prompt-generate-btn"
        className="mt-6 btn-mint rounded-full px-6 py-3 text-sm inline-flex items-center gap-2 disabled:opacity-60"
      >
        <Sparkles size={16} />
        {busy ? "Forging…" : "Forge prompt"}
      </button>

      {(typed || busy) && (
        <div className="mt-8" data-testid="prompt-gen-output-wrap">
          <div className="flex items-center justify-between mb-2">
            <span className="label-eyebrow">Output</span>
            {output && (
              <button onClick={copy} className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1.5" data-testid="prompt-gen-copy">
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? "Copied" : "Copy"}
              </button>
            )}
          </div>
          <pre className={`bg-black border hairline rounded-md p-5 text-[13px] leading-relaxed text-[#9be9c8] whitespace-pre-wrap font-mono ${busy ? "caret" : typed.length < output.length ? "caret" : ""}`} data-testid="prompt-gen-output">
{typed}
          </pre>
        </div>
      )}

      {paywall && (
        <div className="absolute inset-0 z-10 grid place-items-center rounded-2xl backdrop-blur-md bg-black/70 p-6" data-testid="prompt-gen-paywall">
          <div className="text-center max-w-md">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#A855F7]/15 mb-4">
              <Lock size={20} className="text-[#A855F7]" />
            </div>
            <h3 className="text-2xl font-bold tracking-tight font-display-ai mb-2">
              You've used your 3 free generations today.
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              Unlock unlimited Prompt Lab access — plus the full Custom GPT, all 7 modules and the
              Toolkit — with any AI Creator OS tier.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="/academy#tiers"
                onClick={() => setPaywall(false)}
                className="btn-mint rounded-full px-5 py-3 text-sm inline-flex items-center gap-2"
                data-testid="paywall-view-tiers"
              >
                See tiers
              </a>
              {user ? (
                <Link
                  to="/redeem"
                  onClick={() => setPaywall(false)}
                  className="rounded-full px-5 py-3 text-sm border hairline hover:bg-white/5"
                  data-testid="paywall-redeem"
                >
                  I already bought — redeem
                </Link>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setPaywall(false)}
                  className="rounded-full px-5 py-3 text-sm border hairline hover:bg-white/5"
                  data-testid="paywall-login"
                >
                  Sign in
                </Link>
              )}
            </div>
            <button
              onClick={() => setPaywall(false)}
              className="mt-5 text-xs text-gray-500 hover:text-gray-300"
              data-testid="paywall-dismiss"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
